import {
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';

import { createNewCheckIn, submitCheckIn } from '../queries/submitCheckIns';
import { submitFeedbackUsage } from '../queries/submitFeedbackUsage';
import { CheckInData } from '../types/queryTypes';
import { TAssessmentResult } from '../types/types';
// Custom hook for the mutation

export type TSubmitCheckIn = {
  data: CheckInData & {
    userId: string;
    dailySequence: number;
    workLocation?: string;
  };
  assessments: TAssessmentResult[];
};
export function useSubmitCheckIn(): UseMutationResult<
  string,
  Error,
  TSubmitCheckIn
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ data, assessments }: TSubmitCheckIn) => {
      const checkInId =
        data.checkInId ||
        (await createNewCheckIn(
          data.userId,
          data.dailySequence,
          data.workLocation,
        ));
      if (!checkInId) {
        throw new Error('Failed to create check-in');
      }
      const ticketCheckInId = await submitCheckIn({ ...data, checkInId });
      if (assessments.length > 0) {
        await submitFeedbackUsage(ticketCheckInId, assessments);
      }
      return ticketCheckInId;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['userTickets'] }),
        queryClient.invalidateQueries({ queryKey: ['ticketCheckIns'] }),
        queryClient.invalidateQueries({ queryKey: ['checkIns'] }),
        queryClient.invalidateQueries({ queryKey: ['checkInSequence'] }),
        queryClient.invalidateQueries({ queryKey: ['blockers'] }),
        queryClient.invalidateQueries({ queryKey: ['sprintTimeline'] }),
      ]);
    },
    onError: async (error) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['userTickets'] }),
        queryClient.invalidateQueries({ queryKey: ['ticketCheckIns'] }),
        queryClient.invalidateQueries({ queryKey: ['checkIns'] }),
        queryClient.invalidateQueries({ queryKey: ['checkInSequence'] }),
        queryClient.invalidateQueries({ queryKey: ['blockers'] }),
        queryClient.invalidateQueries({ queryKey: ['sprintTimeline'] }),
      ]);
      throw error; // Re-throw the error so it can be handled by the component
    },
  });
}
